import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import EventNoteIcon from '@mui/icons-material/EventNote'
import API from '../../../services/API'

// Journeys planned for a previous day that never reached a terminal status.
// Rendered on the bookings, planning, dispatch, and reports pages so overdue
// work stays visible no matter which date those pages are filtered to.
// Renders nothing when there is nothing outstanding.
const OutstandingJourneys = ({ variant = 'light', onJumpToDate, sx }) => {
  const navigate = useNavigate()
  const [journeys, setJourneys] = useState([])
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    let active = true
    API.get('/pts/outstanding-journeys')
      .then((res) => {
        if (active && res.status === 200 && Array.isArray(res.data)) {
          setJourneys(res.data)
        }
      })
      .catch(() => {})
    return () => {
      active = false
    }
  }, [])

  const dark = variant === 'dark'

  const palette = useMemo(
    () =>
      dark
        ? {
            bg: 'rgba(245,158,11,0.12)',
            border: 'rgba(245,158,11,0.45)',
            heading: '#FCD34D',
            text: 'rgba(255,255,255,0.85)',
            subText: 'rgba(255,255,255,0.6)',
            rowBg: 'rgba(255,255,255,0.05)',
          }
        : {
            bg: '#FFF8E9',
            border: '#F0C36D',
            heading: '#92400E',
            text: 'rgba(15,23,42,0.9)',
            subText: 'rgba(15,23,42,0.6)',
            rowBg: '#fff',
          },
    [dark],
  )

  if (!journeys.length) return null

  const oldest = journeys[0]
  const daysOverdue = (journey) =>
    moment()
      .startOf('day')
      .diff(moment(journey.sortTime).startOf('day'), 'days')

  const jumpToPlanning = (journey) => {
    const day = moment(journey.sortTime)
    if (onJumpToDate) {
      onJumpToDate(day)
    } else {
      navigate(`/pts/planning?date=${day.format('YYYYMMDD')}`)
    }
  }

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: palette.border,
        bgcolor: palette.bg,
        px: { xs: 1.25, md: 2 },
        py: 1.25,
        minWidth: 0,
        ...sx,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        onClick={() => setExpanded((prev) => !prev)}
        sx={{ cursor: 'pointer' }}
      >
        <WarningAmberIcon sx={{ color: palette.heading }} />
        <Box flex={1} minWidth={0}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 800, color: palette.heading, lineHeight: 1.3 }}
          >
            {journeys.length} incomplete{' '}
            {journeys.length === 1 ? 'journey' : 'journeys'} from previous days
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: palette.subText,
              display: 'block',
              whiteSpace: { xs: 'normal', md: 'nowrap' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Oldest: {moment(oldest.sortTime).format('ddd DD MMM')} (
            {daysOverdue(oldest)} {daysOverdue(oldest) === 1 ? 'day' : 'days'}{' '}
            ago). These need completing, cancelling, or marking as no shows.
          </Typography>
        </Box>
        <IconButton
          size="small"
          sx={{
            color: palette.heading,
            transform: expanded ? 'rotate(180deg)' : 'none',
            transition: 'transform 150ms ease',
          }}
          aria-label={
            expanded ? 'Hide outstanding journeys' : 'Show outstanding journeys'
          }
        >
          <ExpandMoreIcon />
        </IconButton>
      </Stack>

      <Collapse in={expanded}>
        <Stack spacing={0.75} mt={1.25}>
          {journeys.map((journey) => {
            const patientName = [
              journey?.patientDoc?.demographics?.firstName,
              journey?.patientDoc?.demographics?.lastName,
            ]
              .filter(Boolean)
              .join(' ')
            const overdue = daysOverdue(journey)

            return (
              <Stack
                key={journey._id}
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  bgcolor: palette.rowBg,
                  borderRadius: 1.5,
                  px: 1.25,
                  py: 0.75,
                  flexWrap: 'wrap',
                  rowGap: 0.5,
                }}
              >
                <Chip
                  size="small"
                  label={`${overdue}d overdue`}
                  color="warning"
                  sx={{ fontWeight: 700, borderRadius: 1 }}
                />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, color: palette.text }}
                >
                  {moment(journey.sortTime).format('DD/MM HH:mm')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: palette.text, minWidth: 0, maxWidth: '100%' }}
                  noWrap
                >
                  {patientName || 'Unknown patient'}
                </Typography>
                <Typography variant="caption" sx={{ color: palette.subText }}>
                  {journey?.bookingDoc?.bookingRef || journey?.journeyRef || ''}
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  label={journey.status}
                  sx={{
                    borderRadius: 1,
                    color: palette.text,
                    borderColor: palette.border,
                  }}
                />
                {journey?.dutyDoc?.dutyRef && (
                  <Typography variant="caption" sx={{ color: palette.subText }}>
                    Duty {journey.dutyDoc.dutyRef}
                  </Typography>
                )}
                {journey?.orgDoc?.orgName && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: palette.subText,
                      minWidth: 0,
                      maxWidth: '100%',
                    }}
                    noWrap
                  >
                    {journey.orgDoc.orgName}
                  </Typography>
                )}
                <Box flex={1} />
                <Tooltip title="Open this day in planning">
                  <Button
                    size="small"
                    color="warning"
                    startIcon={<EventNoteIcon />}
                    onClick={() => jumpToPlanning(journey)}
                    sx={{ fontWeight: 700 }}
                  >
                    Planning
                  </Button>
                </Tooltip>
                {journey?.booking && (
                  <Tooltip title="Open booking">
                    <Button
                      size="small"
                      color="warning"
                      startIcon={<OpenInNewIcon />}
                      onClick={() =>
                        window.open(`/pts/bookings/${journey.booking}`)
                      }
                      sx={{ fontWeight: 700 }}
                    >
                      Booking
                    </Button>
                  </Tooltip>
                )}
              </Stack>
            )
          })}
        </Stack>
      </Collapse>
    </Box>
  )
}

export default OutstandingJourneys
